import type {ProductType} from '../enum/productType'

/* Constants */
export const BUILDING_TYPE_TERRACED: BuildingType = 'terraced'
export const BUILDING_TYPE_BLOCK: BuildingType = 'block'
export const BUILDING_TYPE_DUPLEX: BuildingType = 'duplex'
export const BUILDING_TYPE_DETACHED: BuildingType = 'detached'

/* Types */
export type BuildingType = 'block' | 'terraced' | 'duplex' | 'detached'
export type YesNo = 'yes' | 'no'
export type WaterMeterType = 'cold' | 'warm' | 'both'
export type MeterSize = 'dn15' | 'dn20'
export type SystemType = 'wired' | 'wireless'
export type WirelessMode = 'hydrolinkAir'
export type SmokeDetectorType = 'none' | 'mains' | 'battery'

/* Interfaces */

export interface AdminProduct {
  id: string
  productType: ProductType
  description: string
  priceCents: number | null
}

export interface AdminState {
  products: AdminProduct[]
  productsPending: boolean
  productsError: string
  users: AdminUser[]
  usersPending: boolean
  usersError: string
  notAuthorizedUsers: AdminUser[]
  notAuthorizedUsersPending: boolean
  notAuthorizedUsersError: string
}

export interface AdminUser {
  id: string
  email: string
  auth0Id: string
  useAuthorized: boolean
  isAdmin: boolean
}

export interface AuthState {
  pending: boolean
  error: string | null
  token: string | null
}

export interface Claims {
  email: string
  isAuthorized: boolean
  isAdmin?: boolean
}

/**
 * Design plan state.
 * Note: the object type matches request body format for some backend APIs.
 */
export interface PlanState {
  companyName: string
  buildingType: '' | BuildingType
  buildingQuantity?: number
  stairwellQuantity?: number
  floorQuantity?: number
  apartmentQuantity?: number
  centralUnitPlacedInMiddle: '' | YesNo
  waterMeterType: '' | WaterMeterType
  moreThanOneMeterPerApartment: '' | YesNo
  meterSize: '' | MeterSize
  apartmentScreensEnabled: '' | YesNo
  systemType: '' | SystemType
  temperatureMetersEnabled: '' | YesNo
  wirelessMode: '' | WirelessMode
  smokeDetectorType: SmokeDetectorType
}

export interface ServicePricesState {
  pending: boolean
  error: string
  pricing: {
    hydrolinkApiPrice: number
    hydrolinkPremiumPrice: number,
    vatMultiplier: number
  }
}

export interface ProductsList {
  entries: ProductsListEntry[]
  totalCost: number
}

export interface ProductsListEntry {
  description: string
  quantity: number
}

/**
 * Water meter technology options for a generated products list: 'inductive' is always present,
 * 'ultrasonic' is only available for some plan configurations (e.g. Hydrolink Air / DN15).
 */
export interface ProductsListOptions {
  inductive: ProductsList
  ultrasonic: ProductsList | null
}

export interface ProductsState {
  pending: boolean
  error: string
  list: ProductsListOptions
}

export interface ReportState {
  submittingToAddress: string
  submittedToAddress: string
  error: string
}

export interface UserState {
  isAuthenticated: boolean
  claims: Claims | null
  claimsPending: boolean
  claimsError: string | null
}
